import React, { useEffect, useRef, useState } from "react";
import { Appointment, AppointmentStatus } from "../types";
import "../pages/PurchaseOrders.css";
import "../pages/Appointments.css";

interface Props {
  a: Appointment;
  canUpdate: boolean;
  /** Ekip / başka personel takvimi: kartta personel adı da yazılır. */
  showStaff: boolean;
  busy: boolean;
  /** Üstte gerekçe penceresi açıkken Esc bu kartı kapatmasın. */
  escDisabled?: boolean;
  onClose: () => void;
  onApprove: (a: Appointment) => void;
  onReject: (a: Appointment) => void;
  onCancel: (a: Appointment) => void;
  onMark: (a: Appointment, status: 6 | 7) => void;
}

/** Durum → rozet sınıfı (po-badge). 1 doğrulama · 2 onay bekliyor · 3 onaylı · 4 red · 5 iptal · 6 tamam · 7 gelinmedi */
export const AP_BADGE: Record<AppointmentStatus, string> = { 1: "muted", 2: "warn", 3: "ok", 4: "muted", 5: "muted", 6: "ap-navy", 7: "err" };

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

const fmtDate = (s: string) => new Date(s).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** Randevu kartı: misafir, tür, saat, iletişim ve duruma göre işlemler (onay/ret, iptal, tamamlandı, gelinmedi). */
const AppointmentDetailModal: React.FC<Props> = ({ a, canUpdate, showStaff, busy, escDisabled, onClose, onApprove, onReject, onCancel, onMark }) => {
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy && !escDisabled) { e.stopPropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, busy, escDisabled]);

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  useEffect(() => { closeRef.current?.focus(); }, []);

  return (
    <div className="po-overlay center" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="po-modal" role="dialog" aria-modal="true" aria-labelledby="ap-detail-title">
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">{a.number}{showStaff ? ` · ${a.staffName}` : ""}</span>
            <h3 id="ap-detail-title">{a.guestName}</h3>
            <div className="po-badges">
              <span className={`po-badge ${AP_BADGE[a.status]}`}>{a.statusName}</span>
              <span className="po-badge muted">{a.mode === 2 ? "Çevrim içi" : "Yüz yüze"}</span>
            </div>
          </div>
          <button ref={closeRef} type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={busy}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          <dl className="po-facts">
            <div><dt>Konu</dt><dd>{a.typeName}</dd></div>
            <div><dt>Tarih ve saat</dt><dd>{fmtDate(a.startAt)}<small>{a.localRange} · TSİ</small></dd></div>
            <div className="wide">
              <dt>{a.mode === 2 ? "Görüşme bağlantısı" : "Görüşme adresi"}</dt>
              <dd>
                {a.mode === 2
                  ? (a.meetingUrl ? <a href={a.meetingUrl} target="_blank" rel="noreferrer">{a.meetingUrl}</a> : <small>Bağlantı henüz üretilmedi.</small>)
                  : (a.location || <small>Adres girilmemiş.</small>)}
              </dd>
            </div>
            <div className="wide">
              <dt>İletişim</dt>
              <dd>
                <a href={`mailto:${a.guestEmail}`}>{a.guestEmail}</a>
                {a.guestPhone ? ` · ${a.guestPhone}` : ""}
                {(a.guestCompany || a.firmName) && (
                  <small>{a.guestCompany}{a.guestCompany && a.firmName ? " · " : ""}{a.firmName ? `Kayıtlı firma: ${a.firmName}` : ""}</small>
                )}
              </dd>
            </div>
            {a.guestNote && <div className="wide"><dt>Misafir notu</dt><dd className="ap-quote">“{a.guestNote}”</dd></div>}
            {a.cancelReason && <div className="wide"><dt>Gerekçe</dt><dd>{a.cancelReason}</dd></div>}
          </dl>
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint">Talep: {new Date(a.createdAt).toLocaleDateString("tr-TR", { dateStyle: "medium" })}</span>
          <div className="po-foot-btns">
            {canUpdate && a.status === 2 && <>
              <button type="button" className="po-btn danger" onClick={() => onReject(a)} disabled={busy}>Reddet</button>
              <button type="button" className="po-btn primary" onClick={() => onApprove(a)} disabled={busy}>{busy ? "İşleniyor" : "Onayla"}</button>
            </>}
            {canUpdate && a.status === 3 && <>
              <button type="button" className="po-btn danger" onClick={() => onCancel(a)} disabled={busy}>İptal et</button>
              <button type="button" className="po-btn" onClick={() => onMark(a, 7)} disabled={busy}>Gelinmedi</button>
              <button type="button" className="po-btn primary" onClick={() => onMark(a, 6)} disabled={busy}>Tamamlandı</button>
            </>}
            {!(canUpdate && (a.status === 2 || a.status === 3)) && <button type="button" className="po-btn" onClick={onClose} disabled={busy}>Kapat</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailModal;
